import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Typography, message } from "antd";
import SelectionPoster from "./SelectionPoster";
import copyToClipboard from "../../utils/copy";
import { downloadSelectionPosterFromElement, ensurePosterFontsLoaded } from "../../utils/selectionPoster";
import { useTranslation } from "../../i18n";

const { Paragraph } = Typography;

function SelectionSharePanel({ url, albumName, expiresAtLabel = "", compact = false }) {
    const { t } = useTranslation();
    const posterRef = useRef(null);
    const [posterReady, setPosterReady] = useState(false);
    const [posterError, setPosterError] = useState("");

    useEffect(() => {
        if (!url) {
            setPosterReady(false);
            setPosterError("");
            return;
        }

        setPosterReady(false);
        setPosterError("");

        let cancelled = false;

        async function preparePoster() {
            try {
                await ensurePosterFontsLoaded();
                if (!cancelled) {
                    setPosterReady(true);
                }
            } catch (error) {
                console.error("Poster font loading failed:", error);
                if (!cancelled) {
                    setPosterReady(true);
                }
            }
        }

        preparePoster();

        return () => {
            cancelled = true;
        };
    }, [url, albumName, expiresAtLabel]);

    const handleCopy = useCallback(async () => {
        if (!url) {
            return;
        }
        try {
            await copyToClipboard(url);
            message.success(t("selection.share.linkCopied"));
        } catch {
            message.error(t("selection.share.copyFailed"));
        }
    }, [url, t]);

    const handleDownloadPoster = useCallback(async () => {
        if (!url || !posterRef.current) {
            return;
        }
        try {
            await downloadSelectionPosterFromElement(posterRef.current, albumName);
            message.success(t("selection.share.posterDownloaded"));
        } catch (error) {
            console.error("Poster download failed:", error);
            setPosterError(t("selection.share.posterFailed"));
            message.error(t("selection.share.posterDownloadFailed"));
        }
    }, [url, albumName, t]);

    return (
        <div className={`selection-share${compact ? " selection-share--compact" : ""}`}>
            {!compact && (
                <div className="selection-share__header">
                    <h2 className="selection-share__album">{albumName}</h2>
                    <p className="selection-share__subtitle">{t("selection.share.subtitle")}</p>
                    <p className="selection-share__subtitle-en">{t("selection.share.subtitleEn")}</p>
                </div>
            )}

            <div className="selection-share__url-row">
                <Input readOnly value={url} aria-label={t("selection.share.urlLabel")} />
                <Button type="primary" onClick={handleCopy}>
                    {t("selection.share.copyLink")}
                </Button>
            </div>

            <div className="selection-share__actions">
                <Button
                    onClick={handleDownloadPoster}
                    disabled={!posterReady || Boolean(posterError)}
                >
                    {t("selection.share.downloadPoster")}
                </Button>
            </div>

            <div className="selection-share__poster-wrap">
                {!posterReady && url && (
                    <p className="selection-share__poster-status">{t("selection.share.posterLoading")}</p>
                )}
                {posterError && (
                    <Paragraph type="danger" className="selection-share__poster-status">
                        {posterError}
                    </Paragraph>
                )}
                {url && (
                    <div className={posterReady ? "" : "selection-share__poster--loading"}>
                        <SelectionPoster
                            ref={posterRef}
                            url={url}
                            albumName={albumName}
                            expiresAtLabel={expiresAtLabel}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default SelectionSharePanel;
